import { useMutation } from '@apollo/client'

import {
   convertToErrorType,
   CustomError,
   getAPIStatus
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { CreateThreadParams } from '@flowwai-frontend/bps-officer-web/graphql'
import { ThreadModel } from '@flowwai-frontend/shared-web/ai-chat-support/data-access'

import {
   CreateThreadMutation as Mutation,
   CreateThreadDocument as MutationDocument,
   CreateThreadMutationVariables as MutationVariables
} from './mutation.generatedTypes'
import useAPIResponseHandler from './responseHandler'

export interface FailureArgsType {
   data?: CustomError<Mutation['createThread']>
   error?: Error | null
}

interface TriggerAPIArgsType {
   requestObj: CreateThreadParams

   onFailure?: (args: FailureArgsType) => void
   onSuccess?: (threadModel: ThreadModel) => void
}

export const useCreateThread = () => {
   const [createThread, { error, loading }] = useMutation<
      Mutation,
      MutationVariables
   >(MutationDocument)

   const { handleAPIResponse } = useAPIResponseHandler()

   const triggerAPI = (args: TriggerAPIArgsType): void => {
      createThread({
         fetchPolicy: 'network-only',
         variables: {
            params: args.requestObj
         },
         onError: error => {
            args.onFailure?.({ error })
         },
         onCompleted: data =>
            handleAPIResponse({
               data,
               onFailure: (
                  errorMessage: string,
                  data: Mutation['createThread']
               ) =>
                  args.onFailure?.({
                     data: new CustomError(errorMessage, data)
                  }),
               onSuccess: args.onSuccess
            })
      })
   }

   const apiError = convertToErrorType(error)

   const apiStatus = getAPIStatus({
      isError: Boolean(error),
      isLoading: loading
   })

   return {
      apiError,
      apiStatus,
      triggerAPI
   }
}
